import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { SystemNoticeFile } from '../../../../../services/system-notice.service';
import { preparePostHtml } from '../../../posts/prepare-post-html';
import { InAppLinksDirective } from '../../in-app-links.directive';
import { RtI18nDirective } from '../../runtime-translation.directive';

export interface SystemNoticeMoreInfoDialogData {
  title?: string;
  html?: string;
  files: SystemNoticeFile[];
  allowFeedback: boolean;
}

export interface SystemNoticeMoreInfoDialogResult {
  /** Whether closing the panel should also dismiss the notice behind it. */
  dismiss: boolean;
  feedback?: string;
}

/** Matches NVARCHAR(1000) on dbo.SystemNoticeViews.Feedback. */
const FEEDBACK_MAX_LENGTH = 1000;

/**
 * Side panel opened from a notice's More Info button, shared by the toast and the popup.
 * Submitting feedback closes with it, because the API only accepts feedback on dismissal.
 */
@Component({
  standalone: true,
  selector: 'system-notice-more-info',
  templateUrl: './system-notice-more-info.component.html',
  styleUrl: './system-notice-more-info.component.scss',
  imports: [MatDialogModule, MatFormFieldModule, MatInputModule, FormsModule, InAppLinksDirective, RtI18nDirective]
})
export class SystemNoticeMoreInfoComponent {

  public readonly FEEDBACK_MAX_LENGTH = FEEDBACK_MAX_LENGTH;

  public readonly safeHtml: SafeHtml;
  public feedback = '';
  public isSubmitting = false;

  constructor(
    private dialogRef: MatDialogRef<SystemNoticeMoreInfoComponent, SystemNoticeMoreInfoDialogResult>,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: SystemNoticeMoreInfoDialogData
  ) {
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(preparePostHtml(this.data.html));
  }

  /** Blob-only and already SAS-signed by the read, so a chip can link straight at it. */
  public fileUrl(file: SystemNoticeFile): string {
    return file.SecureUrl ?? file.BlobUrl;
  }

  public fileLabel(file: SystemNoticeFile): string {
    return file.Title?.trim() || file.FileName;
  }

  public get charsRemaining(): number {
    return FEEDBACK_MAX_LENGTH - this.feedback.length;
  }

  public close(): void {
    this.dialogRef.close();
  }

  /** Dismiss without feedback. */
  public dismissNotice(): void {
    if (this.isSubmitting) { return; }

    this.dialogRef.close({ dismiss: true });
  }

  // The caller dismisses the notice with this, so the panel never reopens.
  public submit(): void {
    if (!this.feedback.trim() || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.dialogRef.close({ dismiss: true, feedback: this.feedback.trim() });
  }
}
